import { apiError, errorMessageFrom } from "../../../../../../../utils/formErrors";
import { ensureOrgShifts } from "../../../../../../../services/useOrgData";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useLocation, useNavigate } from "react-router";
import TopBar from "../../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import FormField from "../../../../../../../component/Formfield";
import SectionLoader from "../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../component/EmptyState";
import ErrorMsg from "../../../../../../../component/ErrorMsg";
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from "../../../../../../../component/wizard";
import { useNotify } from "../../../../../../../component/NotificationProvider";

import { getShiftRotationById, updateShiftRotation } from "../../../../../../../services/shiftRotation";
import { tokens } from "../../../../../../../theme";

const toDateInput = (v) => (v ? new Date(v).toISOString().split("T")[0] : "");

const EditRotation = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const rotationId = state?.rotationId;

  const [loading, setLoading] = useState(true);
  const [apiCall, setApiCall] = useState(false);
  const [error, setError] = useState("");
  const [shiftList, setShiftList] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", rotationType: "Weekly", rotationFrequency: "",
    startDate: "", status: "active", shifts: [],
  });

  const shiftNameById = useMemo(() => {
    const map = {};
    (shiftList || []).forEach((s) => { map[s._id] = s.name; });
    return map;
  }, [shiftList]);

  useEffect(() => {
    if (!rotationId) { setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const [res, shiftsRes] = await Promise.all([
          getShiftRotationById(rotationId),
          ensureOrgShifts().catch(() => null),
        ]);
        if (!active) return;
        if (res?.success === false) throw apiError(res, "Failed to load rotation");
        const d = res?.data || {};
        // rotation shifts come back populated ({_id,name}); merge with full list for the name map
        const populated = (d.shifts || []).filter((s) => typeof s === "object");
        setShiftList([...(Array.isArray(shiftsRes) ? shiftsRes : []), ...populated]);
        setForm({
          name: d.name || "", description: d.description || "",
          rotationType: d.rotationType || "Weekly",
          rotationFrequency: d.rotationFrequency ?? "",
          startDate: toDateInput(d.startDate), status: d.status || "active",
          shifts: (d.shifts || []).map((s) => (typeof s === "object" ? s._id : s)),
        });
      } catch (e) {
        if (active) notify.error(e?.message || "Failed to load rotation");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [rotationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => { setForm((p) => ({ ...p, [field]: value })); setError(""); };

  const handleSave = async () => {
    setApiCall(true);
    try {
      const res = await updateShiftRotation(rotationId, {
        name: form.name, description: form.description,
        rotationType: form.rotationType, rotationFrequency: Number(form.rotationFrequency),
        startDate: form.startDate || undefined, status: form.status,
      });
      if (res.success) {
        notify.success("Shift rotation updated");
        navigate("/app/shift-rotation/listing");
      } else {
        setError(res.message);
        notify.error(errorMessageFrom(res, "Could not update rotation"));
      }
    } catch (e) {
      notify.error(e?.message || "Something went wrong");
    } finally {
      setApiCall(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Shift Rotation"} breadcrumbs={[{ label: "Shift Rotation", href: "" }, { label: "Edit", href: "" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: "1.25rem" }}>
          <BtnSecondary to={"/app/shift-rotation/listing"} sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        {loading ? (
          <SectionLoader />
        ) : !rotationId ? (
          <EmptyState title="No rotation selected" />
        ) : (
          <>
            <FormSection title="Rotation details">
              <FieldGrid>
                <Box>
                  <FieldLabel>Rotation Name</FieldLabel>
                  <FormField type="text" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Status</FieldLabel>
                  <FormField type="select" options={["active", "inactive"]} value={form.status} onChange={(e) => handleChange("status", e.target.value)} />
                </Box>
              </FieldGrid>
              <Box sx={{ mt: "1.25rem" }}>
                <FieldLabel>Description</FieldLabel>
                <FormField type="text" value={form.description} fullwidth row={3} onChange={(e) => handleChange("description", e.target.value)} />
              </Box>
            </FormSection>

            <FormSection title="Cadence">
              <FieldGrid>
                <Box>
                  <FieldLabel>Rotation Type</FieldLabel>
                  <FormField type="select" options={["Weekly", "Monthly"]} value={form.rotationType} onChange={(e) => handleChange("rotationType", e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Rotation Frequency</FieldLabel>
                  <FormField type="text" value={form.rotationFrequency} endAdornment={form.rotationType === "Monthly" ? "Months" : "Weeks"}
                    onChange={(e) => handleChange("rotationFrequency", e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Start Date</FieldLabel>
                  <Box component="input" type="date" value={form.startDate} onChange={(e) => handleChange("startDate", e.target.value)}
                    sx={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: "0.875rem", color: tokens.text,
                      border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "11px 14px",
                      background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` } }} />
                </Box>
              </FieldGrid>
            </FormSection>

            <FormSection title="Shifts in rotation" description="Employees rotate through these shifts in order.">
              {form.shifts.length ? (
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                  {form.shifts.map((id, i) => (
                    <React.Fragment key={id}>
                      <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text, background: `${tokens.accentTint}`,
                        border: `1px solid ${tokens.accent}33`, borderRadius: "8px", px: 1.5, py: 0.5 }}>
                        {shiftNameById[id] || "Unknown shift"}
                      </Box>
                      {i < form.shifts.length - 1 && <ArrowForwardIcon sx={{ fontSize: "1rem", color: tokens.textMuted }} />}
                    </React.Fragment>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>No shifts in this rotation.</Box>
              )}
            </FormSection>

            <ErrorMsg>{error}</ErrorMsg>
            <WizardActions>
              <BtnPrimary onClick={handleSave} disabled={apiCall} sx={{ borderRadius: "8px" }}>
                {apiCall ? <CircularProgress size={"1.25rem"} /> : "Save Changes"}
              </BtnPrimary>
            </WizardActions>
          </>
        )}
      </WizardCard>
    </Box>
  );
};

export default EditRotation;
